// ============================================================
//  StoreProducts.js — product list + "Add / edit product" with photos
//  Up to 4 photos per product (first = cover). A live preview shows the
//  card exactly as customers see it in WhatsApp.
// ============================================================
import React, { useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { storeAction, uploadStoreImage } from '../../api/client';
import { naira, Switch } from './storeUi';

const MAX_PHOTOS = 4;
const blank = { id: '', name: '', price: '', salePrice: '', category: '', stock: '', description: '', photos: [], active: true, variantLabel: 'Size', variants: [] };
const MAX_OPTIONS = 10;
const toForm = p => ({ ...p, price: String(p.price), salePrice: p.salePrice ? String(p.salePrice) : '', stock: p.stock == null ? '' : String(p.stock),
  variantLabel: p.variantLabel && p.variantLabel !== 'Option' ? p.variantLabel : (p.variants?.length ? 'Option' : 'Size'),
  variants: (p.variants || []).map(v => ({ ...v, price: v.price == null ? '' : String(v.price), stock: v.stock == null ? '' : String(v.stock) })) });

export default function StoreProducts({ bId, store, products, categories = [], onChange, onCategories }) {
  const [editing, setEditing] = useState(null);
  const [editCat, setEditCat] = useState(null);
  const [cat, setCat] = useState('All');
  const [q, setQ] = useState('');
  const names = useMemo(() => categories.map(c => c.name), [categories]);
  const shown = products.filter(p => (cat === 'All' || p.category === cat) && (!q || p.name.toLowerCase().includes(q.toLowerCase())));
  const countOf = name => products.filter(p => p.category === name).length;

  async function toggle(p, on) {
    onChange(products.map(x => (x.id === p.id ? { ...x, active: on } : x)));
    try { const res = await storeAction('product_active', bId, { prId: p.id, on }); toast.success(res.data.message); }
    catch (e) { toast.error('Could not update'); onChange(products); }
  }
  async function move(i, d) {
    const list = [...categories];
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    onCategories(list);
    try { const res = await storeAction('category_order', bId, { ids: list.map(c => c.id) }); onCategories(res.data.categories); }
    catch (e) { toast.error('Could not reorder'); }
  }
  // a product saved with a new category name creates that category — refresh the list
  async function refreshCategories(p) {
    if (!names.includes(p.category)) {
      try { const res = await storeAction('category_order', bId, { ids: categories.map(c => c.id) }); onCategories(res.data.categories); } catch (e) { /* not critical */ }
    }
  }

  return (
    <div className="stp-col">
      <section className="stp-card" aria-labelledby="stp-cats">
        <div className="stp-card-head">
          <div><h2 id="stp-cats">Categories</h2><p className="stp-muted">Customers pick a category first in WhatsApp, then swipe through its product photos. Use the arrows to set the order.</p></div>
          <button type="button" className="stp-btn stp-btn-ghost stp-btn-sm" onClick={() => setEditCat({ id: 0, name: '', emoji: '' })}><i className="fa-solid fa-plus" /> New category</button>
        </div>
        {categories.length === 0 ? <p className="stp-muted">No categories yet — create one (e.g. Bags, Shoes), then add products to it.</p> : (
          <div className="stp-catrow">
            {categories.map((c, i) => (
              <div key={c.id} className="stp-cat">
                <button type="button" className="stp-cat-img" onClick={() => setEditCat(c)} aria-label={`Edit ${c.name}`}>
                  <span aria-hidden="true">{c.emoji || '🛍️'}</span>
                </button>
                <b>{c.name}</b>
                <small>{countOf(c.name)} product{countOf(c.name) === 1 ? '' : 's'}</small>
                <span className="stp-cat-move">
                  <button type="button" aria-label={`Move ${c.name} left`} disabled={i === 0} onClick={() => move(i, -1)}><i className="fa-solid fa-chevron-left" /></button>
                  <button type="button" aria-label={`Move ${c.name} right`} disabled={i === categories.length - 1} onClick={() => move(i, 1)}><i className="fa-solid fa-chevron-right" /></button>
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="stp-card" aria-label="Products">
        <div className="stp-toolbar">
          <label className="stp-search"><i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
            <span className="stp-sr">Search products</span>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search products" />
          </label>
          <div className="stp-seg" role="group" aria-label="Category">
            {['All', ...names].map(c => <button key={c} type="button" aria-pressed={cat === c} className={cat === c ? 'is-on' : ''} onClick={() => setCat(c)}>{c}</button>)}
          </div>
          <button type="button" className="stp-btn stp-btn-brand" onClick={() => setEditing({ ...blank, category: cat !== 'All' ? cat : names[0] || '' })}><i className="fa-solid fa-plus" /> Add product</button>
        </div>

        {products.length === 0 ? (
          <div className="stp-empty-inline">
            <p><b>No products yet.</b> Add your first one — a clear square photo, a name and a price is all it takes.</p>
            <button type="button" className="stp-btn stp-btn-dark" onClick={() => setEditing({ ...blank, category: names[0] || '' })}>Add product</button>
          </div>
        ) : (
          <div className="stp-table-wrap">
            <table className="stp-table">
              <thead><tr><th>Product</th><th>Category</th><th>Price</th><th>Stock</th><th>Sold</th><th>In store</th><th><span className="stp-sr">Edit</span></th></tr></thead>
              <tbody>
                {shown.map(p => (
                  <tr key={p.id}>
                    <td><span className="stp-prod">
                      <span className="stp-thumb">{p.photos[0] ? <img src={p.photos[0]} alt="" /> : <i className="fa-regular fa-image" aria-hidden="true" />}{p.photos.length > 1 && <em>{p.photos.length}</em>}</span>
                      <span><b>{p.name}</b>{p.description && <small>{p.description}</small>}</span>
                    </span></td>
                    <td>{p.category}</td>
                    <td>{p.salePrice ? <><s className="stp-was">{naira(p.price)}</s><b>{naira(p.salePrice)}</b></> : <b>{naira(p.price)}</b>}
                      {p.variants?.length > 0 && <small className="stp-muted stp-block">{p.variants.length} {(p.variantLabel || 'option').toLowerCase()}s</small>}</td>
                    <td><Stock stock={p.stock} /></td>
                    <td>{p.sold}</td>
                    <td><Switch on={p.active} onChange={on => toggle(p, on)} label={`Show ${p.name} in store`} /></td>
                    <td><button type="button" className="stp-btn stp-btn-ghost stp-btn-sm" onClick={() => setEditing(toForm(p))}>Edit</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editing && <ProductEditor bId={bId} store={store} cats={names} initial={editing}
        onClose={() => setEditing(null)}
        onSaved={p => { onChange(products.some(x => x.id === p.id) ? products.map(x => (x.id === p.id ? p : x)) : [...products, p]); setEditing(null); refreshCategories(p); }}
        onDeleted={id => { onChange(products.filter(x => x.id !== id)); setEditing(null); }} />}
      {editCat && <CategoryEditor bId={bId} initial={editCat} count={countOf(editCat.name)} onClose={() => setEditCat(null)}
        onSaved={(list, renamedFrom, renamedTo) => {
          onCategories(list); setEditCat(null);
          if (renamedFrom && renamedFrom !== renamedTo) onChange(products.map(p => (p.category === renamedFrom ? { ...p, category: renamedTo } : p)));
        }} />}
    </div>
  );
}

const EMOJIS = ['🛍️','👜','👟','👠','👗','👕','👖','🧢','⌚','💍','🕶️','🧴','💄','💇‍♀️','📱','💻','🎧','🔌','🍲','🍗','🍔','🍕','🎂','🍿','🥤','🍎','🛒','🍼','🧸','📚','🎁','🛋️','🏋️','💊','💐','🐾','🚗','📶','🔥','⭐','🆕','🏷️'];

function CategoryEditor({ bId, initial, count, onClose, onSaved }) {
  const [name, setName] = useState(initial.name);
  const [emoji, setEmoji] = useState(initial.emoji || '');
  const [busy, setBusy] = useState('');
  const [confirmDel, setConfirmDel] = useState(false);
  async function save() {
    setBusy('save');
    try {
      const res = await storeAction('category_save', bId, { category: { id: initial.id || undefined, name: name.trim(), emoji } });
      toast.success('Category saved');
      onSaved(res.data.categories, initial.name, name.trim());
    } catch (e) { toast.error(e?.response?.data?.message || 'Could not save'); }
    setBusy('');
  }
  async function del() {
    if (!confirmDel) { setConfirmDel(true); return; }
    setBusy('del');
    try { const res = await storeAction('category_delete', bId, { id: initial.id }); toast.success(res.data.message); onSaved(res.data.categories); }
    catch (e) { toast.error(e?.response?.data?.message || 'Could not delete'); setBusy(''); setConfirmDel(false); }
  }
  const shown = emoji || initial.emoji || '🛍️';
  return (
    <div className="stp-modal" role="dialog" aria-modal="true" aria-labelledby="stp-ce-title" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="stp-modal-box stp-ce">
        <div className="stp-modal-head">
          <h2 id="stp-ce-title">{initial.id ? 'Edit category' : 'New category'}</h2>
          <button type="button" className="stp-x" aria-label="Close" onClick={onClose}><i className="fa-solid fa-xmark" /></button>
        </div>
        <div className="stp-ce-body">
          <label className="stp-field">Name<input value={name} maxLength={60} onChange={e => setName(e.target.value)} placeholder="e.g. Bags" autoFocus /></label>
          <div className="stp-field" role="group" aria-labelledby="stp-ce-emoji">
            <span id="stp-ce-emoji">Emoji <small className="stp-muted">· shown next to the name in WhatsApp</small></span>
            <div className="stp-emojis">
              {EMOJIS.map(e => <button key={e} type="button" aria-pressed={shown === e} className={shown === e ? 'is-on' : ''} onClick={() => setEmoji(e)}>{e}</button>)}
            </div>
            <input value={emoji} maxLength={8} onChange={e => setEmoji(e.target.value)} placeholder="Or type / paste any emoji" aria-label="Custom emoji" className="stp-emoji-in" />
          </div>
          <div className="stp-ce-prev" aria-label="WhatsApp preview">
            <span className="stp-pe-k">In WhatsApp</span>
            <div className="stp-ce-row"><b>{shown} {name.trim() || 'Category'}</b><small>{count} item{count === 1 ? '' : 's'} · from ₦…</small></div>
          </div>
        </div>
        <div className="stp-modal-foot">
          {initial.id ? <button type="button" className={`stp-btn stp-btn-sm ${confirmDel ? 'stp-btn-dangerfill' : 'stp-btn-danger'}`} onBlur={() => setConfirmDel(false)} onClick={del} disabled={busy !== '' || count > 0}
            title={count > 0 ? 'Move or delete its products first' : ''}>{confirmDel ? 'Tap again to delete' : 'Delete'}</button> : null}
          <span className="stp-flex" />
          <button type="button" className="stp-btn stp-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="stp-btn stp-btn-dark" onClick={save} disabled={busy !== '' || !name.trim()}>{busy === 'save' ? <span className="stp-spin" /> : null}Save</button>
        </div>
      </div>
    </div>
  );
}

function Stock({ stock }) {
  if (stock == null) return <span className="stp-pill is-ok">In stock</span>;
  if (stock <= 0) return <span className="stp-pill is-bad">Sold out</span>;
  return <span className={`stp-pill ${stock <= 5 ? 'is-warn' : 'is-ok'}`}>{stock} left</span>;
}

function ProductEditor({ bId, store, cats, initial, onClose, onSaved, onDeleted }) {
  const [p, setP] = useState(initial);
  const [uploads, setUploads] = useState([]);   // [{ key, pct, preview }]
  const [busy, setBusy] = useState('');
  const [confirmDel, setConfirmDel] = useState(false);
  const [newCat, setNewCat] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setP(x => ({ ...x, [k]: v }));
  const num = v => String(v).replace(/[^\d.]/g, '');
  const setOpt = (i, k, v) => setP(x => ({ ...x, variants: x.variants.map((o, n) => (n === i ? { ...o, [k]: v } : o)) }));
  const addOpt = () => setP(x => ({ ...x, variants: [...x.variants, { id: '', name: '', price: '', stock: '' }] }));
  const delOpt = i => setP(x => ({ ...x, variants: x.variants.filter((_, n) => n !== i) }));
  const slots = MAX_PHOTOS - p.photos.length - uploads.length;

  async function addFiles(files) {
    const list = Array.from(files || []).slice(0, Math.max(0, slots));
    for (const file of list) {
      if (!/^image\/(jpeg|png|webp)$/.test(file.type)) { toast.error(`${file.name}: use a JPG, PNG or WebP photo`); continue; }
      if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name} is over 5 MB`); continue; }
      const key = `${Date.now()}${Math.random()}`;
      const preview = URL.createObjectURL(file);
      setUploads(u => [...u, { key, pct: 0, preview }]);
      try {
        const res = await uploadStoreImage(bId, 'product', file, ev => {
          const pct = ev.total ? Math.round((ev.loaded / ev.total) * 100) : 50;
          setUploads(u => u.map(x => (x.key === key ? { ...x, pct } : x)));
        });
        setP(x => ({ ...x, photos: [...x.photos, res.data.url].slice(0, MAX_PHOTOS) }));
      } catch (e) { toast.error(e?.response?.data?.message || 'Upload failed'); }
      setUploads(u => u.filter(x => x.key !== key));
      URL.revokeObjectURL(preview);
    }
    if (fileRef.current) fileRef.current.value = '';
  }
  const removePhoto = i => set('photos', p.photos.filter((_, n) => n !== i));
  const makeCover = i => set('photos', [p.photos[i], ...p.photos.filter((_, n) => n !== i)]);

  async function save() {
    if (!String(p.category || '').trim()) { toast.error('Choose a category'); return; }
    setBusy('save');
    try {
      const variants = p.variants.filter(o => o.name.trim()).map(o => ({ id: o.id || undefined, name: o.name.trim(), price: o.price === '' ? null : Number(o.price), stock: o.stock === '' ? null : Number(o.stock) }));
      const res = await storeAction('product_save', bId, { product: { ...p, price: Number(num(p.price)), salePrice: p.salePrice === '' ? null : Number(num(p.salePrice)),
        stock: p.stock === '' ? '' : Number(p.stock), variants, variantLabel: p.variantLabel } });
      toast.success(p.id ? 'Product updated' : 'Product added — it’s live in your store');
      onSaved(res.data.product);
    } catch (e) { toast.error(e?.response?.data?.message || 'Could not save'); }
    setBusy('');
  }
  async function del() {
    if (!confirmDel) { setConfirmDel(true); return; }
    setBusy('del');
    try { await storeAction('product_delete', bId, { prId: p.id }); toast.success('Deleted'); onDeleted(p.id); }
    catch (e) { toast.error('Could not delete'); setBusy(''); }
  }

  const price = Number(num(p.price)) || 0;
  const sale = Number(num(p.salePrice)) || 0;
  const onSale = sale > 0 && sale < price;
  const optPrices = p.variants.filter(o => o.name.trim()).map(o => Number(o.price) > 0 ? Number(o.price) : (onSale ? sale : price));
  const fromPrice = optPrices.length ? Math.min(...optPrices) : (onSale ? sale : price);
  const hasOpts = p.variants.some(o => o.name.trim());
  return (
    <div className="stp-modal" role="dialog" aria-modal="true" aria-labelledby="stp-pe-title" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="stp-modal-box stp-pe">
        <div className="stp-modal-head">
          <h2 id="stp-pe-title">{p.id ? 'Edit product' : 'Add a product'}</h2>
          <button type="button" className="stp-x" aria-label="Close" onClick={onClose}><i className="fa-solid fa-xmark" /></button>
        </div>
        <div className="stp-pe-body">
          <div className="stp-pe-form">
            <div className="stp-field">
              <span>Photos <small>· up to {MAX_PHOTOS}, the first is the cover</small></span>
              <div className="stp-photos">
                {p.photos.map((url, i) => (
                  <div key={url} className="stp-photo">
                    <img src={url} alt={`Photo ${i + 1}`} />
                    {i === 0 ? <span className="stp-cover-tag">Cover</span>
                      : <button type="button" className="stp-make-cover" onClick={() => makeCover(i)}>Make cover</button>}
                    <button type="button" className="stp-photo-x" aria-label={`Remove photo ${i + 1}`} onClick={() => removePhoto(i)}><i className="fa-solid fa-xmark" /></button>
                  </div>
                ))}
                {uploads.map(u => (
                  <div key={u.key} className="stp-photo is-up">
                    <img src={u.preview} alt="Uploading" />
                    <span className="stp-up"><i style={{ width: `${u.pct}%` }} /></span>
                  </div>
                ))}
                {slots > 0 && (
                  <label className="stp-photo-add">
                    <i className="fa-solid fa-camera" aria-hidden="true" />Add photo<small>camera or gallery</small>
                    <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={e => addFiles(e.target.files)} />
                  </label>
                )}
              </div>
              <small className="stp-hint">Square photos look best in WhatsApp. Max 5 MB each.</small>
            </div>
            <label className="stp-field">Product name<input value={p.name} maxLength={150} onChange={e => set('name', e.target.value)} placeholder="e.g. Jollof + chicken" /></label>
            <div className="stp-grid3">
              <label className="stp-field">Price (₦)<input inputMode="decimal" value={p.price} onChange={e => set('price', e.target.value)} placeholder="4500" /></label>
              <label className="stp-field">Category
                {newCat || !cats.length ? (
                  <input value={p.category} maxLength={60} onChange={e => set('category', e.target.value)} placeholder="New category, e.g. Bags" autoFocus={newCat} />
                ) : (
                  <select value={cats.includes(p.category) ? p.category : ''} onChange={e => { if (e.target.value === '__new') { setNewCat(true); set('category', ''); } else set('category', e.target.value); }}>
                    <option value="" disabled>Choose…</option>
                    {cats.map(c => <option key={c} value={c}>{c}</option>)}
                    <option value="__new">+ New category…</option>
                  </select>
                )}</label>
              {hasOpts ? (
                <div className="stp-field"><span>Stock</span><small className="stp-hint">Set per {(p.variantLabel || 'option').toLowerCase()} below</small></div>
              ) : (
                <label className="stp-field">Stock<input inputMode="numeric" value={p.stock} onChange={e => set('stock', e.target.value.replace(/\D/g, ''))} placeholder="Unlimited" />
                  <small className="stp-hint">Leave empty if it never runs out</small></label>
              )}
            </div>
            <label className="stp-field">Sale price (₦) <small>· optional — shows the old price crossed out</small>
              <input inputMode="decimal" value={p.salePrice} onChange={e => set('salePrice', e.target.value)} placeholder="Not on sale" />
              {sale > 0 && sale >= price && <small className="stp-hint stp-danger-t">Must be lower than the price</small>}</label>

            <div className="stp-field" role="group" aria-labelledby="stp-opts-k">
              <span id="stp-opts-k">Sizes, colours or other options <small>· optional, up to {MAX_OPTIONS}</small></span>
              {p.variants.length === 0 ? (
                <button type="button" className="stp-btn stp-btn-ghost stp-btn-sm stp-self-start" onClick={addOpt}><i className="fa-solid fa-plus" /> Add options (e.g. sizes 40, 41, 42)</button>
              ) : (
                <div className="stp-opts">
                  <label className="stp-opt-label">Options are called
                    <input value={p.variantLabel} maxLength={30} onChange={e => set('variantLabel', e.target.value)} placeholder="Size" list="stp-opt-names" />
                    <datalist id="stp-opt-names"><option value="Size" /><option value="Colour" /><option value="Storage" /><option value="Flavour" /><option value="Type" /></datalist>
                  </label>
                  <div className="stp-opt-head" aria-hidden="true"><span>{p.variantLabel || 'Option'}</span><span>Price (₦)</span><span>Stock</span><span /></div>
                  {p.variants.map((o, i) => (
                    <div key={i} className="stp-opt">
                      <input aria-label={`${p.variantLabel || 'Option'} ${i + 1}`} value={o.name} maxLength={40} onChange={e => setOpt(i, 'name', e.target.value)} placeholder={i === 0 ? 'e.g. 42 · Black' : ''} />
                      <input aria-label="Price" inputMode="decimal" value={o.price} onChange={e => setOpt(i, 'price', num(e.target.value))} placeholder={naira(onSale ? sale : price)} />
                      <input aria-label="Stock" inputMode="numeric" value={o.stock} onChange={e => setOpt(i, 'stock', e.target.value.replace(/\D/g, ''))} placeholder="∞" />
                      <button type="button" className="stp-icon" aria-label={`Remove ${o.name || 'option'}`} onClick={() => delOpt(i)}><i className="fa-regular fa-trash-can" /></button>
                    </div>
                  ))}
                  {p.variants.length < MAX_OPTIONS && <button type="button" className="stp-btn stp-btn-ghost stp-btn-sm stp-self-start" onClick={addOpt}><i className="fa-solid fa-plus" /> Add another</button>}
                  <small className="stp-hint">Leave price empty to use the product price. Customers pick one after tapping “Add to cart”.</small>
                </div>
              )}
            </div>
            <label className="stp-field">Short description <small>· shown under the photo</small>
              <textarea value={p.description || ''} maxLength={500} onChange={e => set('description', e.target.value)} placeholder="What’s in it, size, how many it serves…" /></label>
            <div className="stp-row"><span><b>Show in store</b><small>Turn off to hide it without deleting</small></span><Switch on={p.active} onChange={v => set('active', v)} label="Show in store" /></div>
          </div>

          <div className="stp-pe-preview" aria-label="WhatsApp preview">
            <span className="stp-pe-k">Live preview · WhatsApp</span>
            <div className="stp-wa">
              <div className="stp-wa-card">
                <div className="stp-wa-img">{p.photos[0] ? <img src={p.photos[0]} alt="" /> : <span>Add a photo</span>}</div>
                <div className="stp-wa-body"><b>{p.name || 'Product name'}</b>
                  <b>{onSale && fromPrice === sale && <s>{naira(price)}</s>} {optPrices.length && Math.min(...optPrices) !== Math.max(...optPrices) ? 'from ' : ''}{naira(fromPrice)}
                    {hasOpts ? ` · ${p.variants.filter(o => o.name.trim()).length} ${(p.variantLabel || 'option').toLowerCase()}s` : ''}</b>
                  {p.description && <span>{p.description}</span>}
                  {!hasOpts && p.stock !== '' && Number(p.stock) > 0 && Number(p.stock) <= 5 && <i>Only {p.stock} left</i>}</div>
                <div className="stp-wa-btn">Add to cart</div>
                <div className="stp-wa-btn">Details</div>
              </div>
              <small>One swipeable card in the {p.category || 'category'} carousel customers see in {store.name}.</small>
            </div>
          </div>
        </div>
        <div className="stp-modal-foot">
          {p.id && <button type="button" className={`stp-btn stp-btn-sm ${confirmDel ? 'stp-btn-dangerfill' : 'stp-btn-danger'}`} onBlur={() => setConfirmDel(false)} onClick={del} disabled={busy !== ''}>
            {confirmDel ? 'Tap again to delete' : 'Delete'}</button>}
          <span className="stp-flex" />
          <button type="button" className="stp-btn stp-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="stp-btn stp-btn-dark" onClick={save} disabled={busy !== '' || uploads.length > 0}>
            {busy === 'save' ? <span className="stp-spin" /> : null}{uploads.length ? 'Uploading…' : 'Save product'}</button>
        </div>
      </div>
    </div>
  );
}
